import { CSSProperties, forwardRef, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { InputProps } from './input-text.types';

export const SUFFIX_RIGHT_PADDING = 16;
export const SUFFIX_CONTENT_GAP = 8;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      hasError,
      type = 'text',
      readOnly,
      disabled,
      showBorder = true,
      cursorClassName,
      paddingLeftClassName = 'pl-4',
      className = '',
      isChangingInactiveColor = true,
      widthClassName = 'w-full',
      helperText,
      suffix,
      style,
      ...props
    },
    ref
  ) => {
    const suffixRef = useRef<HTMLSpanElement>(null);
    const [suffixPaddingRight, setSuffixPaddingRight] = useState<number | undefined>();

    useLayoutEffect(() => {
      if (!suffix || !suffixRef.current) {
        setSuffixPaddingRight(undefined);
        return;
      }
      setSuffixPaddingRight(suffixRef.current.offsetWidth + SUFFIX_RIGHT_PADDING + SUFFIX_CONTENT_GAP);
    }, [suffix]);

    const cursorInput = cursorClassName ?? 'cursor-default';
    const isActive = !readOnly && !disabled;

    const inputStyle: CSSProperties | undefined =
      suffix && suffixPaddingRight !== undefined ? { ...style, paddingRight: `${suffixPaddingRight}px` } : style;

    return (
      <div className='flex flex-col grow'>
        <div className='relative flex items-center w-full'>
          <input
            readOnly={readOnly}
            disabled={disabled}
            ref={ref}
            className={classNames(
              'text-body-m focus:text-subtitle-m',
              'placeholder-neutral-50 dark:placeholder-neutral-10 placeholder:text-body-m',
              { 'fill-primary focus:fill-primary': showBorder, 'bg-transparent': !showBorder },
              'rounded-md',
              {
                'border-soft focus:border-hard hover:border-hard': !hasError,
                'border-critical-soft focus:border-critical-soft hover:border-critical-soft': hasError,
                'cursor-not-allowed border-soft': !isActive,
                'text-tertiary': !isActive && isChangingInactiveColor,
                'text-primary': isActive || (!isActive && !isChangingInactiveColor),
                'border-0': !showBorder,
                'border ': showBorder,
              },
              `${cursorInput} ${className} ${widthClassName} py-3 pr-4 focus:outline-hidden focus:ring-0`,
              paddingLeftClassName
            )}
            style={inputStyle}
            type={type}
            aria-invalid={hasError || undefined}
            {...props}
          />
          {suffix ? (
            <span
              ref={suffixRef}
              aria-hidden='true'
              className='absolute right-4 z-10 text-secondary text-body-m pointer-events-none whitespace-nowrap'>
              {suffix}
            </span>
          ) : null}
        </div>
        {helperText && !hasError && <p className='mt-1 text-xs text-secondary'>{helperText}</p>}
      </div>
    );
  }
);
